import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Checkbox,
} from "@mui/material";
import ModalContainer from "../ui-elements/modal-container";
import { CloseDrawerIcon } from "../ui-elements/svg-icons";
import { useTheme } from "@mui/material/styles";
import useInfiniteScrollFetcher from "../../hooks/useInfiniteScrollFetcher";
import { RepresentativeDataList } from "../../interface/representative";
import { apiGetRepresentativeList } from "../../api/representative";
import { FC, useState } from "react";
import { btn_value } from "../dashboard/data";
import { btnCover } from "../styles-ui";
import BadgeImage from "./badge-image";
import useInfiniteScrollRepresentativeFetcher from "../../hooks/useInfiniteScrollReps";
import { FilterDataProp } from "./search-input";

export const SearchRepsModal = ({
  open,
  modal_handler,
  get_reps_info,
  filter_data
}: {
  open: boolean;
  modal_handler: () => void;
  get_reps_info: (value: { id: number; name: string }) => void;
  filter_data: FilterDataProp
}) => {
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box
        sx={{
          background: "#fff",
          py: 2,
          px: 4,
          borderRadius: "16px",
          height: 600,
        }}
      >
        <Stack direction={"row"} justifyContent={"end"}>
          <Button sx={{ minWidth: 10 }} onClick={modal_handler}>
            <CloseDrawerIcon />
          </Button>
        </Stack>
        <RepresentativeSearchContainer
          get_representative={get_reps_info}
          modal_handler={modal_handler}
          filter_data={filter_data}
        />
      </Box>
    </ModalContainer>
  );
};

const initialParams = {
  page: 1,
  page_size: 10,
  search: "",
  state: "",
  local_government: "",
  position: "",
  sort_by: "asc",
  sort_order: "",
};

const RepresentativeSearchContainer = ({
  get_representative,
  modal_handler,
  filter_data
}: {
  get_representative: (value: { id: number; name: string }) => void;
  modal_handler: () => void;
  filter_data: FilterDataProp
}) => {
  const theme = useTheme();

  const { items, sentryRef, loading, updateParams } =
    useInfiniteScrollRepresentativeFetcher<RepresentativeDataList>({
      queryKey: "representative-data-list",
      fetchData: apiGetRepresentativeList,
      initialParams: {
        page_size: 10,
        search: "",
        state: filter_data?.selected_state,
        local_government: filter_data?.selected_lga,
        position: "",
        sort_by: "",
        sort_order: "",
      },
    });

  const [selectedButtons, setSelectedButtons] = useState("All");
  const [selectedInfo, setSelectedInfo] = useState<{
    id: number;
    name: string;
  }>({ id: 0, name: "" });

  const handleButtonClick = (value: string) => {
    setSelectedButtons(value);
    if (value === "All") {
      const param = { ...initialParams, position: "" };
      handleFilterChange(param);
      return;
    }
    //  update filter
    const param = { ...initialParams, position: value?.toLowerCase() };
    handleFilterChange(param);
  };
  // Example of updating search
  const handleSearchChange = (searchTerm: string) => {
    updateParams({ search: searchTerm });
  };

  // Example of updating multiple params
  const handleFilterChange = (filters: Partial<typeof initialParams>) => {
    updateParams(filters);
  };

  const [selectedIds, setSelectedIds] = useState<any[]>([]);

  // Handle checkbox selected
  const handleRepsSelected = (value: { id: number; name: string }) => {
    setSelectedInfo({ id: value.id, name: value.name });
    get_representative({ id: value.id, name: value.name });
    modal_handler();
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(231, 235, 239, 1)",
        mt: 2,
        borderRadius: "8px",
        px: 2,
      }}
    >
      <Typography sx={{ fontWeight: 520, mt: 2 }} variant="h6" color="black">
        Your representatives
      </Typography>

      <Box sx={{ display: "flex", overflowX: "auto", my: 2 }}>
        {btn_value?.map((item, index) => (
          <Box
            key={index}
            sx={{
              ...btnCover,
              backgroundColor: selectedButtons === item ? "#459173" : "#E2F1EB",
              color: selectedButtons === item ? "white" : "#637D92",
            }}
            onClick={() => handleButtonClick(item)}
          >
            <Typography
              variant="caption"
              sx={{ whiteSpace: "nowrap", lineHeight: "10px" }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
      {!loading && items?.length === 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 500, textAlign: "center" }}
          >
            No data found
          </Typography>
        </Box>
      )}
      <Box sx={{ height: items?.length === 0 ? 100 : 500, overflowY: "auto" }}>
        {items?.map((reps, id) => (
          <Stack key={reps?.id} sx={{ cursor: "pointer" }}>
            <RepresentativeSearchCover
              key={id}
              id={reps?.id}
              image={""}
              name={reps.name}
              party={reps.party}
              position={reps.position}
              badge={2}
              selectedInfo={selectedInfo}
              handleCheckbox={handleRepsSelected}
            />
          </Stack>
        ))}
      </Box>
      <div ref={sentryRef} style={{}}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </div>
    </Box>
  );
};

const RepresentativeSearchCover: FC<{
  image: any;
  name: string;
  party: string;
  position: string;
  badge: number;
  selectedInfo: { id: number; name: string };
  handleCheckbox: (value: { id: number; name: string }) => void;
  id: number; // Placeholder for representative ID
}> = ({
  image,
  badge,
  name,
  party,
  position,
  id,
  selectedInfo,
  handleCheckbox,
}) => {
  const value = { id: id, name: name };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        my: 2,
      }}
    >
      {/* Checkbox */}
      <Checkbox
        checked={selectedInfo.id === id}
        onChange={() => handleCheckbox(value)}
      />
      <Stack>
        <Stack direction={"row"} sx={{ gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} color="black">
            {name}
          </Typography>
          <BadgeImage badge={badge} />
        </Stack>
        <Typography variant="caption">{party}</Typography>
        <Typography variant="caption">{position}</Typography>
      </Stack>
    </Box>
  );
};
